import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { textsApi } from '../../utils/api';

export function useReaderDocument(textId, navigate) {
  const [readerData, setReaderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [textError, setTextError] = useState(null); // 'not_found' | 'error' | null
  const current = useRef(null);
  current.current = readerData;
  const request = useRef(0);
  const moreBusy = useRef(false);
  const controllers = useRef(new Set());
  useEffect(() => () => {
    request.current += 1;
    controllers.current.forEach(controller => controller.abort());
    controllers.current.clear();
    moreBusy.current = false;
  }, [textId]);

  const fetchReaderData = useCallback(async () => {
    const version = ++request.current;
    const controller = new AbortController();
    controllers.current.add(controller);
    setLoading(true);
    setLoadingMore(false);
    setTextError(null);
    moreBusy.current = false;
    try {
      const { data } = await textsApi.getReaderData(textId, { signal: controller.signal });
      if (version === request.current) setReaderData(data);
    } catch (error) {
      if (!controller.signal.aborted && version === request.current) {
        const status = error?.response?.status;
        if (status === 404) {
          setTextError('not_found');
        } else {
          setTextError('error');
          toast.error('Failed to load text');
          navigate('/dashboard');
        }
      }
    } finally {
      controllers.current.delete(controller);
      if (version === request.current) setLoading(false);
    }
  }, [textId, navigate]);

  const loadMore = useCallback(async () => {
    const next = current.current?.pagination;
    if (!next?.has_more || moreBusy.current || current.current?.text?.text_id !== textId) return;
    moreBusy.current = true;
    setLoadingMore(true);
    const version = request.current;
    const controller = new AbortController();
    controllers.current.add(controller);
    try {
      const { data } = await textsApi.getReaderData(textId, { params: { token_offset: next.next_offset }, signal: controller.signal });
      if (version === request.current) setReaderData(previous => previous?.pagination?.next_offset === next.next_offset ? { ...previous, tokens: [...previous.tokens, ...data.tokens], pagination: data.pagination } : previous);
    } catch (error) {
      if (!controller.signal.aborted && version === request.current) toast.error('Could not load the next section. Press Next to retry.');
    } finally {
      controllers.current.delete(controller);
      if (version === request.current) { moreBusy.current = false; setLoadingMore(false); }
    }
  }, [textId]);
  return { readerData, setReaderData, loading, loadingMore, fetchReaderData, loadMore, textError };
}